<template>
  <v-data-table
    class="ui-data-table loader-fullscreen"
    id="table-cool"
    :class="[getClass(), customTableClass]"
    hide-default-footer
    :hide-default-header="!!$scopedSlots.header"
    v-bind="$attrs"
    :mobile-breakpoint="200"
    v-on="$listeners"
    :headers="filteredHeaders"
    :items="items"
    :no-data-text="$t('base.noData')"
    :header-props="headerProps"
    :options="options"
    :loading="loading"
    :show-select="showSelect"
    :item-class="getRowClass"
    :style="cssVars"
    @item-selected="handleItemSelection"
  >
    <template #progress>
      <v-progress-linear indeterminate color="blue500" v-if="loading"></v-progress-linear>
    </template>
    <template v-slot:top="{ options }">
      <div v-if="title && !hideTop" class="pt-5 px-6 pb-2 font-weight-bold text-h6 black--text text--darken-6">
        {{ title }}
      </div>
      <slot name="reportTitle"></slot>
      <v-container
        v-if="!hideTop"
        fluid
        class="px-4 px-sm-6"
        :class="title || $slots.reportTitle ? 'py-1' : 'py-4 pt-sm-5'"
      >
        <v-row dense class="flex-wrap flex-container justify-space-between">
          <v-col v-if="$slots.prependInner" cols="12" class="d-flex justify-space-between">
            <slot name="prependInner" />
          </v-col>

          <v-col v-if="$slots.title" cols="12">
            <slot name="title" />
          </v-col>

          <v-col v-if="$slots.filters" cols="12" class="filters-custom-columns">
            <slot name="filters" />
          </v-col>

          <v-col cols="12" class="d-flex flex-column flex-md-row justify-start justify-md-start">
            <v-text-field
              class="search-table"
              v-if="!hideSearch"
              :value="searchField"
              background-color="black lighten-7"
              filled
              dense
              solo
              flat
              prepend-inner-icon="mdi-magnify"
              hide-details
              clearable
              :placeholder="searchPlaceholder || $t('base.search')"
              @input="onInputSearchQuery"
            />

            <slot name="afterSearch" />

            <div
              class="d-flex justify-space-between align-center align-self-sm-start mt-3 mt-md-0 ml-0 flex-lg-row"
              :class="paginationCenter ? 'w-100' : paginationLeft ? '' : 'ml-md-auto align-center'"
            >
              <div class="d-flex align-self-start search-select-wrapper align-center">
                <span class="limit-pages" v-if="!hidePagination && $vuetify.breakpoint.smAndUp">{{
                  $t('base.onPage')
                }}</span>
                <v-select
                  v-if="!hidePagination"
                  v-model="options.pagination.limit"
                  :items="itemsLimit"
                  dense
                  filled
                  outlined
                  hide-details="auto"
                  append-icon="mdi-chevron-down"
                  background-color="black lighten-7"
                  @change="onChangePaginationLimit(options)"
                  class="px-2 search-select"
                />
                <div class="limit-pages" v-if="!hidePagination && !$scopedSlots.reportTitle">
                  <span class="limit-pages pr-sm-8">{{ $t('base.from') }} {{ options.total }}</span>
                </div>
              </div>

              <ui-pagination
                v-if="!hidePagination"
                class="table-pagination"
                v-model="options.pagination"
                :total-items="options.totalItems"
                @input="
                  $emit('update:options', { ...options, page: options.pagination.page })
                  $emit('reFetchData')
                "
              />
            </div>
            <div v-if="isBtnAction" class="ml-3">
              <slot name="action" />
            </div>
          </v-col>
        </v-row>
      </v-container>

      <ui-table-actions
         v-if="isTableActionsShown"
        :selected-count="displaySelectedCount"
        @close="unselectAll"
      >
        <slot name="actions" />
      </ui-table-actions>
    </template>
    <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
      <span v-if="'tooltip' in header" :key="index"
        >{{ header.text }}
        <v-icon
          size="14"
          v-tooltip="{
            content: header.tooltip,
            classes: 'tooltip-smaller',
          }"
          >mdi-information-slab-circle-outline</v-icon
        >
      </span>
      <span :key="index" v-else v-html="header.text.split('|').join(`<br>`)"></span>
    </template>
    <template v-for="(header, index) in headers" v-slot:[`item.${header.value}`]="{ item, value }">
      <router-link
        class="slot-link"
        v-if="editPageName && item?.id"
        :key="index"
        :to="{ name: editPageName, params: { id: item.id } }"
      >
        <span v-if="header.cropLength" v-tooltip="isCropped(value, header.cropLength) ? value : undefined">
          {{ value | cropString(header.cropLength) }}
        </span>
        <div v-else>{{ value }}</div>
      </router-link>
      <span v-else-if="header.cropLength" :key="index" v-tooltip="isCropped(value, header.cropLength) ? value : undefined">
        {{ value | cropString(header.cropLength) }}
      </span>
      <span :key="index" v-else>{{ value }}</span>
    </template>

    <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
      <router-link
        class="slot-link"
        :class="getSlotJustifyClass(slot)"
        v-if="editPageName && scope?.item?.id"
        :key="slot"
        :to="{ name: editPageName, params: { id: scope.item.id } }"
        ><div><slot :name="slot" v-bind="scope" /></div
      ></router-link>
      <!-- body is overridden by expandable lists: they render own row checkboxes with these helpers -->
      <slot
        v-else-if="slot === 'body'"
        :name="slot"
        v-bind="{ ...scope, isRowSelected: isSelected, toggleRowSelection: toggleItemSelection }"
      />
      <slot v-else :name="slot" v-bind="scope" />
    </template>
    <template #header.data-table-select="{ on }">
      <v-popover
        placement="right"
        :container="`.ui-data-table${customTableClass ? `.${customTableClass}` : ''}`"
        trigger="manual"
        popover-class="ui-table-selection-popover"
        :open="hasSomeSelected && (!hideSelectedCount || showSelectedCount || isPopoverHovered)"
        :auto-hide="false"
      >
        <v-simple-checkbox
          :value="isAllSelected"
          :indeterminate="isSomeSelected"
          color="primary"
          v-on="on"
          :ripple="false"
          class="ui-table-checkbox"
          :class="{ 'ui-table-checkbox--indeterminate': isSomeSelected }"
          @input="onSelectionToggle"
        />

        <template #popover>
          <ui-card class="px-4 py-2" @mouseenter="isPopoverHovered = true" @mouseleave="isPopoverHovered = false">
            <div class="d-flex">
              <div class="text-body-1">
                {{ $t('base.selected') }}
                <span class="primary--text">{{ displaySelectedCount }}</span>
                {{ $tc('base.items', displaySelectedCount) }}.
              </div>

              <div
                v-if="!disablePopoverBtn"
                class="primary--text text-body-1 font-weight-bold ml-2 cursor-pointer"
                @click="selectAll"
              >
                {{ $t('base.selectAll') }} ({{ totalItems }})
              </div>

              <div
                class="primary--text text-body-1 font-weight-bold ml-2 cursor-pointer clear-selection-btn"
                @click="unselectAll"
              >
                {{ $t('base.clearAll') }}
              </div>
            </div>
          </ui-card>
        </template>
      </v-popover>
    </template>

    <template v-slot:item.data-table-select="{ item, select }">
      <ui-checkbox
        :value="isSelected(item)"
        @input="select($event)"
        @click.stop
        tabindex="0"
        @focus="handleFocus"
        @focusout.native="handleUnfocus"
      />
    </template>
    <template v-slot:body.append>
      <slot name="body.append"></slot>
    </template>
  </v-data-table>
</template>

<script>
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import UiTableActions from '@/components/ui/framework/general/UITableActions.vue'

export default {
  name: 'ui-date-table',
  components: {
    UiTableActions,
  },
  props: {
    itemsLimit: {
      type: Array,
      default: () => {
        return [10, 20, 50, 100, 250, 500]
      },
    },
    headers: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    columnsToShow: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    paginationLeft: {
      type: Boolean,
      default: false,
    },
    firstGroupRowsQty: {
      type: Number,
      default: 0,
    },
    fixedHeaderNum: {
      type: Number,
      default: 0,
    },
    paginationCenter: {
      type: Boolean,
      default: false,
    },
    hideTop: {
      type: Boolean,
      default: false,
    },
    showTableActions: {
      type: Boolean,
      default: false,
    },
    hideSearch: {
      type: Boolean,
      default: false,
    },
    hidePagination: {
      type: Boolean,
      default: false,
    },
    isList: {
      type: Boolean,
      default: false,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    selectionExclusive: {
      type: Boolean,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      default: '',
    },
    // function for disable possibility to select item
    selectableCondition: {
      type: Function,
      default: null,
    },
    selectableKey: {
      type: String,
      default: 'id',
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    showSelect: {
      type: Boolean,
      default: false,
    },
    fixedColsNoBg: {
      type: Boolean,
      default: false,
    },
    height100vh: {
      type: Boolean,
      default: false,
    },
    isBtnAction: {
      type: Boolean,
      default: false,
    },
    hideBoxShadow: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    reportTitle: {
      type: String,
      default: '',
    },
    customTableClass: {
      type: [String, Boolean],
      default: false,
    },
    disablePopoverBtn: {
      type: Boolean,
      default: false,
    },
    headerProps: {
      type: Object,
      default: () => ({
        // for default desc sort icon must be mdi-chevron-down
        sortIcon: 'mdi-chevron-up',
      }),
    },
    editPageName: {
      type: [String, Boolean],
      default: false,
    },
    noTotal: {
      type: Boolean,
      default: false,
    },
    hideSelectedCount: {
      type: Boolean,
      default: false,
    },
    // several selectable tables on one page: the fixed selection panel is shown for one of them only
    hideTableActions: {
      type: Boolean,
      default: false,
    },
    fixHeaderLastChild: {
      type: Boolean,
      default: false,
    },
    amountFixedColsStart: {
      type: Number,
      default: 0,
    },
    fixedColsKey: {
      type: Number,
      default: 0,
    },
    searchUpdateKey: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      searchField: '',
      filteredHeaders: [],
      showSelectedCount: false,
      isPopoverHovered: false,
      // shift+click selects the whole range between the previous and the current checkbox
      isShiftPressed: false,
      lastToggledItem: null,
    }
  },
  computed: {
    cssVars() {
      return {
        '--height': `calc(var(--vh, 1vh) * 100) - ${147 + 'px'}`,
        '--height-mini': `calc(var(--vh, 1vh) * 100) - ${124 + 'px'}`,
      }
    },
    itemsWithEnabledSelection() {
      return this.items.filter(item => !this.checkIfItemSelectionDisabled(item))
    },
    isPageItemsSelected() {
      return this.itemsWithEnabledSelection.every(item =>
        this.selectionExclusive ? !this.checkItemSelected(item) : this.checkItemSelected(item),
      )
    },
    isAllSelected() {
      return (
        (this.selectionExclusive && this.selectedItemsCount === 0) ||
        (this.selectedItemsCount === this.totalItems && this.totalItems > 0)
      )
    },
    selectedItemsCount() {
      return this.selectedItems.length
    },
    displaySelectedCount() {
      return !this.selectionExclusive ? this.selectedItemsCount : this.totalItems - this.selectedItemsCount
    },
    hasSomeSelected() {
      return this.selectedItemsCount !== 0 || this.selectionExclusive
    },
    isSomeSelected() {
      return this.hasSomeSelected && !this.isAllSelected
    },
    isTableActionsShown() {
      return !this.hideTableActions && ((this.hasSomeSelected && this.showSelect) || this.showTableActions)
    },
  },
  watch: {
    headers() {
      this.setHeaders(this.columnsToShow)
    },
    columnsToShow(data) {
      this.setHeaders(data)
    },
    fixedColsKey() {
      if (this.amountFixedColsStart) {
        this.$nextTick(() => {
          this.fixColumns(this.amountFixedColsStart)
        })
      }
    },
    searchUpdateKey() {
      this.searchField = this.options.search
    },
    displayedItems() {
      if (this.amountFixedColsStart) {
        this.$nextTick(() => {
          this.fixColumns(this.amountFixedColsStart)
        })
      }
    },
  },
  created() {
    this.setHeaders(this.columnsToShow)
    this.searchField = this.options.search
  },
  mounted() {
    if (this.amountFixedColsStart) this.fixColumns(this.amountFixedColsStart)
    const table = document.querySelector('.v-data-table.ui-data-table .v-data-table__wrapper')
    table.id = 'table1233'
    this.$el.addEventListener('mousedown', this.onCheckboxMouseDown, true)
    this.$el.addEventListener('click', this.rememberShiftKey, true)
  },
  beforeDestroy() {
    this.$el.removeEventListener('mousedown', this.onCheckboxMouseDown, true)
    this.$el.removeEventListener('click', this.rememberShiftKey, true)
  },
  methods: {
    getSlotJustifyClass(slot) {
      const value = typeof slot === 'string' && slot.startsWith('item.') ? slot.slice('item.'.length) : slot
      const header = this.headers.find(item => item.value === value)
      if (header?.align === 'end') return 'slot-link--justify-end'
      if (header?.align === 'center') return 'slot-link--justify-center'
      return ''
    },
    isCropped(value, length) {
      return typeof value === 'string' && value.length > length
    },
    handleFocus() {
      this.showSelectedCount = true
    },
    handleUnfocus() {
      this.showSelectedCount = false
    },
    fixColumns(num) {
      const table = this.$el.querySelector('.ui-data-table table')
      if (!table) return
      const headers = this.fixHeaderLastChild
        ? table.querySelectorAll('thead tr:last-child th')
        : table.querySelectorAll('thead tr th')
      const rows = table.querySelectorAll('tbody tr')

      if (this.fixedHeaderNum) {
        const startIndex = this.fixedHeaderNum - 1
        const isMaxWidthCol = el => [...el.classList].some(cls => cls.startsWith('max-width-'))

        let leftOffset = 0

        for (let i = startIndex; i < headers.length; i++) {
          const header = headers[i]
          if (i !== startIndex && !isMaxWidthCol(header)) break
          const match = header.className.match(/max-width-(\d+)/)
          if (!match) return
          const width = match[1]

          Object.assign(header.style, {
            position: 'sticky',
            left: `${leftOffset}px`,
            background: 'white',
            zIndex: 25,
          })

          rows.forEach((rowEl, rowIndex) => {
            const cells = rowEl.querySelectorAll('td')
            const cell = cells[i]
            if (!cell) return

            Object.assign(cell.style, {
              position: 'sticky',
              left: `${leftOffset}px`,
              zIndex: 24,
              background: this.fixedColsNoBg ? '' : rowIndex === rows.length - 1 && !this.noTotal ? '#f7f7f7' : 'white',
            })
          })
          leftOffset += width
        }
      } else {
        // Read all header widths before any DOM writes to avoid layout thrashing
        const headerWidths = []
        for (let i = 0; i < num; i++) {
          headerWidths.push(headers[i] ? headers[i].offsetWidth : 0)
        }
        let sum = 0
        for (let i = 0; i < num; i++) {
          if (headers[i]) {
            headers[i].style.position = 'sticky'
            headers[i].style.left = sum - i + 'px' // -i for fix gap btw cols
            headers[i].style.background = 'white'
            headers[i].style.zIndex = '25'
            sum += headerWidths[i]
          }
        }
        rows.forEach((tableRow, index) => {
          let localSum = 0
          const row = tableRow.querySelectorAll('td')
          for (let i = 0; i < num; i++) {
            if (row[i]) {
              row[i].style.position = 'sticky'
              row[i].style.left = localSum - i + 'px' // -i for fix gap btw cols
              if (!this.fixedColsNoBg)
                row[i].style.background = index === rows.length - 1 && !this.noTotal ? '#f7f7f7' : 'white'
              row[i].style.zIndex = '24'
              localSum += headerWidths[i] || 0
            }
          }
        })
      }
      this.applyMaxWidthFromClass(table)
    },
    applyMaxWidthFromClass(table) {
      table.querySelectorAll('[class*="max-width-"]').forEach(el => {
        const match = el.className.match(/max-width-(\d+)/)
        if (!match) return
        const width = match[1]
        el.style.maxWidth = width + 'px'
        el.style.minWidth = width + 'px'
        el.style.width = width + 'px'
        let text = el.querySelector('div a')
        if (!text) return
        text.style.maxWidth = width - 16 + 'px'
        text.style.overflow = 'hidden'
        text.style.whiteSpace = 'nowrap'
        text.style.textOverflow = 'ellipsis'
      })
    },
    onInputSearchQuery: debounce(function (query) {
      this.$emit('update:search', query)
    }, 300),
    setHeaders(selectedHeaders = []) {
      if (!selectedHeaders.length) {
        this.filteredHeaders = [...this.headers]
        return
      }

      const colIDs = selectedHeaders.map(selectedItem => selectedItem.value)
      this.filteredHeaders = this.headers.filter(item => colIDs.includes(item.value))
    },
    getClass() {
      return [
        { 'ui-data-table-clickable': this.$listeners['click:row'] || this.editPageName },
        { 'ui-loading': this.loading },
        { 'ui-box-shadow': this.hideBoxShadow },
        { 'table-screen-height': this.height100vh },
      ]
    },
    onChangePaginationLimit(options) {
      this.$emit('update:options', {
        ...options,
        pagination: {
          ...options.pagination,
          page: 1,
        },
        page: 1,
        itemsPerPage: options.pagination.limit,
      })
      this.$emit('reFetchData')
    },
    onSelectionToggle() {
      if (this.isPageItemsSelected) {
        if (this.selectionExclusive) {
          return this.handleCurrentPageSelection(this.items)
        }
        this.handleCurrentPageDeselection(this.items)
      } else {
        if (this.selectionExclusive) {
          return this.handleCurrentPageDeselection(this.items)
        }
        this.handleCurrentPageSelection(this.items)
      }
    },
    checkIfItemSelectionDisabled(item) {
      return typeof this.selectableCondition === 'function' && !this.selectableCondition(item)
    },
    findItemIndexInSelected(item) {
      return this.selectedItems.findIndex(selectedItem => selectedItem[this.selectableKey] === item[this.selectableKey])
    },

    checkItemSelected(item) {
      const index = this.findItemIndexInSelected(item)
      return index !== -1
    },
    handleCurrentPageSelection(items) {
      const selectedItems = this.selectedItems
      const itemsToAdd = items.filter(item => !this.checkItemSelected(item) && !this.checkIfItemSelectionDisabled(item))

      this.updateSelected([...selectedItems, ...itemsToAdd])
    },
    handleCurrentPageDeselection(items) {
      this.updateSelected(
        this.selectedItems.filter(item => {
          return !items.find(unselectedItem => unselectedItem[this.selectableKey] === item[this.selectableKey])
        }),
      )
    },
    updateSelected(updatedSelectedItems) {
      this.$emit('update:selected-items', updatedSelectedItems)
    },
    selectAll() {
      this.resetSelection()
      this.setSelectedAll(true)
    },
    unselectAll() {
      this.resetSelection()
      this.setSelectedAll(false)
      this.$emit('update:unselect')
    },
    resetSelection() {
      this.updateSelected([])
    },
    setSelectedAll(value = false) {
      this.$emit('update:selection-exclusive', value)
    },
    rememberShiftKey(event) {
      this.isShiftPressed = event.shiftKey
    },
    // shift+mousedown would start a text selection over the rows
    onCheckboxMouseDown(event) {
      if (event.shiftKey && event.target.closest('.v-data-table__checkbox, .v-simple-checkbox')) event.preventDefault()
    },
    getItemsRange(from, to) {
      const indexOf = item => this.items.findIndex(row => row[this.selectableKey] === item[this.selectableKey])
      const fromIndex = indexOf(from)
      const toIndex = indexOf(to)
      if (fromIndex === -1 || toIndex === -1) return [to]
      return this.items.slice(Math.min(fromIndex, toIndex), Math.max(fromIndex, toIndex) + 1)
    },
    handleItemSelection({ item }) {
      const range = this.isShiftPressed && this.lastToggledItem ? this.getItemsRange(this.lastToggledItem, item) : [item]
      const targetState = !this.isSelected(item)
      const updatedSelectedItems = [...this.selectedItems]

      range.forEach(rangeItem => {
        if (this.checkIfItemSelectionDisabled(rangeItem) || this.isSelected(rangeItem) === targetState) return
        const index = updatedSelectedItems.findIndex(
          selectedItem => selectedItem[this.selectableKey] === rangeItem[this.selectableKey],
        )
        if (index === -1) updatedSelectedItems.unshift(rangeItem)
        else updatedSelectedItems.splice(index, 1)
      })

      this.lastToggledItem = item
      this.updateSelected(updatedSelectedItems)
    },
    isSelected(item) {
      return this.selectionExclusive ? !this.checkItemSelected(item) : this.checkItemSelected(item)
    },
    toggleItemSelection(item) {
      if (this.checkIfItemSelectionDisabled(item)) return
      this.handleItemSelection({ item })
    },
    getRowClass(item) {
      const itemClass = this.$attrs['item-class'] || this.$attrs.itemClass
      const ownClass = typeof itemClass === 'function' ? itemClass(item) : get(item, itemClass)
      const isRowSelected = this.showSelect && !this.checkIfItemSelectionDisabled(item) && this.isSelected(item)
      return [ownClass, { 'ui-row-selected': isRowSelected }]
    },
  },
}
</script>

<style lang="scss">
.filters-custom-columns {
  flex: 20 0 auto;
}
.table-screen-height .v-data-table__wrapper {
  max-height: calc(100vh - 10px);
}
.ui-data-table {
  // pos: relative must have for correct the "select all" popover position
  position: relative;
  border-radius: 16px;
  box-shadow: 0px 1px 10px rgba(188, 188, 188, 0.12), 0px 4px 5px rgba(188, 188, 188, 0.14),
    0px 2px 4px -1px rgba(188, 188, 188, 0.2);
  &.ui-box-shadow {
    box-shadow: none;
  }
  .v-data-table__wrapper > table > thead > tr:last-child > th {
    white-space: nowrap;
  }

  .v-data-table__expand-icon {
    color: var(--v-blue500-base);
  }

  .v-data-table__wrapper {
    //overflow-x: inherit;
    overflow-y: inherit;
    & > table {
      padding: 0;
    }

    &::-webkit-scrollbar {
      width: 4px;
      height: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--v-black-darken3);
      border-radius: 4px;
    }

    &::-webkit-scrollbar-track {
      background: #f3f3f3;
    }

    scrollbar-color: var(--v-black-darken3) #f3f3f3;
    scrollbar-width: thin;
  }

  thead > tr > th {
    & > span {
      color: #aeaeae;
      font-weight: 500;
      font-size: 12px;
      line-height: 20px;
      & > i {
        color: #aeaeae !important;
      }
    }
  }

  tbody > tr {
    td {
      color: #343434;
      font-weight: 400;
      font-size: 12px !important;
      line-height: 20px;
      height: 40px !important;
    }
  }

  thead > tr:last-child > th {
    border-bottom: 1px var(--v-black-lighten6) solid !important;
  }

  tbody > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
    background: white !important;
    transform: scale(1, 1);
  }

  tbody > tr:focus-within {
    z-index: 10 !important;
    position: relative;
  }
  .v-data-table__wrapper
    tbody
    > tr:last-child:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
    box-shadow: none !important;
    background: transparent !important;
  }

  tbody > tr:not(:last-child) > td:not(.v-data-table__mobile-row),
  .theme--light.v-data-table
    > .v-data-table__wrapper
    > table
    > tbody
    > tr:not(:last-child)
    > th:not(.v-data-table__mobile-row) {
    border-bottom: 1px var(--v-black-lighten6) solid !important;
  }

  .search-table {
    @media #{map-get($display-breakpoints, 'xs-only')} {
      max-width: 100%;
    }
    max-width: 240px;

    .mdi {
      font-size: 16px;
      color: var(--v-black-lighten2);
    }

    .v-input__slot,
    .v-input__control {
      border-radius: 8px;
      min-height: 36px !important;
    }

    .v-text-field__slot {
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0em;
      text-align: left;

      input {
        color: var(--v-black-darken3);
        padding-bottom: 4px;
      }

      input::placeholder {
        color: var(--v-black-lighten2) !important;
      }
    }
  }

  .search-select-wrapper {
    //margin-left: auto;
    //align-items: center;

    .limit-pages {
      font-size: 12px !important;
      font-weight: 400;
      line-height: 12px !important;
      letter-spacing: 0em;
      text-align: left;
      color: var(--v-black-darken1);
    }
  }

  .search-select {
    width: 74px;
    max-height: 30px;
    border-radius: 8px;
    fieldset,
    .v-text-field .v-input__control {
      border-color: var(--v-black-lighten6);
    }
    .v-select__slot {
      .v-label {
        font-size: 14px !important;
        color: var(--v-black-darken6);
      }
      .v-select__selections {
        font-size: 12px;
        color: var(--v-black-darken6);

        .v-chip {
          margin-top: 5px;
        }
      }
      .v-icon {
        font-size: 20px;
        color: var(--v-black-lighten2);
      }
    }
    .v-input__control {
      .v-select__selections {
        height: 30px;

        .v-select__selection--comma {
          margin-right: 0;
          color: var(--v-black-darken6) !important;
        }
      }

      .v-input__slot {
        padding: 0 8px !important;
        min-height: 30px !important;

        .v-input__append-inner {
          margin-top: 6px;
          padding-left: 0;

          .v-input__icon {
            height: 18px;

            &--append {
              min-width: 18px;
              width: 18px;
            }
          }
        }

        .v-icon {
          font-size: 16px;
        }
      }
    }
  }

  .search-field {
    width: 220px;
  }
  .nested-table {
    .v-data-table-header {
      visibility: collapse;
    }
  }
  .table-pagination > ul {
    justify-content: flex-start;
  }
}
// Selected rows highlight, shared by every table with bulk selection (td: some cells have inline background)
.ui-data-table tbody > tr.ui-row-selected > td {
  background-color: var(--v-primary50-base) !important;
}
.ui-data-table-clickable {
  tbody > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
    cursor: pointer;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  }
}

.ui-loading {
  th.sortable,
  th.sortable i {
    pointer-events: none !important;
  }
}

.tooltip.ui-table-selection-popover {
  // under the modals (z-index 500), above the table
  z-index: 100;
}

.ui-table-checkbox {
  .v-input--selection-controls__input {
    height: 16px;
    width: 16px;
  }

  .v-icon {
    font-size: 20px;
    color: var(--v-black-lighten3);
  }

  &--indeterminate {
    .v-icon {
      color: var(--v-black-darken2);
    }
  }
}
.slot-link {
  // Stretch the link over the td horizontal padding so the whole cell is clickable
  --slot-link-cell-padding: 16px;
  display: flex;
  align-items: center;
  margin: 0 calc(-1 * var(--slot-link-cell-padding));
  padding: 0 var(--slot-link-cell-padding);
  height: 100%;
  color: #343434 !important;
  text-decoration: none;

  &--justify-end {
    justify-content: flex-end;
  }

  &--justify-center {
    justify-content: center;
  }
}
</style>
